import { config } from '../../config';
import { responsivePositionTranslator } from '../../services/responsive/ResponsivePositionTranslator';
import { ComponentType } from '../../types';
import type {
  BreakpointOverride,
  Component,
  ComponentInput,
  Position,
  ResponsivePosition,
  Size,
} from '../../types';
import { isRecord } from '../../utils';
import type {
  CoreEditorSlice,
  EditorImmerGetState,
  EditorImmerSetState,
} from './editor-store-types';

export interface SketchRectangle {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  annotation?: string;
  stroke?: string;
  fill?: string;
  selected?: boolean;
}

export interface AnnotationData {
  rectangleId: string;
  text: string;
  componentType?: string;
  properties?: Record<string, unknown>;
  autoDetected?: boolean;
}

export interface SketchSlice {
  // Sketch/Plan mode state
  sketches: SketchRectangle[];
  sketchAnnotations: Map<string, AnnotationData>;
  selectedSketchId: string | null;
  drawingTool: 'select' | 'rectangle' | 'pan';
  isDrawingMode: boolean;
  isSelectingComponentForSketch: boolean;

  // Sketch/Plan mode actions
  addSketch: (sketch: SketchRectangle) => void;
  updateSketch: (id: string, updates: Partial<SketchRectangle>) => void;
  deleteSketch: (id: string) => void;
  selectSketch: (id: string | null) => void;
  clearSketches: () => void;
  setDrawingTool: (tool: 'select' | 'rectangle' | 'pan') => void;
  toggleDrawingMode: () => void;
  updateSketchAnnotation: (sketchId: string, annotation: AnnotationData) => void;
  generateComponentsFromSketches: () => Promise<void>;
  setSelectingComponentForSketch: (selecting: boolean) => void;
  selectComponentTypeForSketch: (componentType: ComponentType) => void;
}

export const sketchInitialState: Pick<
  SketchSlice,
  | 'sketches'
  | 'sketchAnnotations'
  | 'selectedSketchId'
  | 'drawingTool'
  | 'isDrawingMode'
  | 'isSelectingComponentForSketch'
> = {
  // Sketch/Plan mode initial state
  sketches: [],
  sketchAnnotations: new Map(),
  selectedSketchId: null,
  drawingTool: 'select',
  isDrawingMode: false,
  isSelectingComponentForSketch: false,
};

type ParsedSketchGeneratedComponent = Partial<Omit<ComponentInput, 'type'>> & {
  type?: string;
};

interface ParsedAnnotationProcessResponse {
  success: boolean;
  pattern?: boolean;
  components?: ParsedSketchGeneratedComponent[];
  component?: ParsedSketchGeneratedComponent;
  error?: string;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isResponsiveStrategy(
  value: unknown,
): value is ResponsivePosition['strategy'] {
  return (
    value === 'absolute' ||
    value === 'relative' ||
    value === 'grid' ||
    value === 'flex' ||
    value === 'auto'
  );
}

function isPositionValue(value: unknown): value is Position {
  return (
    isRecord(value) &&
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    (value.z === undefined || typeof value.z === 'number')
  );
}

function isSizeValue(value: unknown): value is Size {
  return (
    isRecord(value) &&
    (typeof value.width === 'number' || typeof value.width === 'string') &&
    (typeof value.height === 'number' || typeof value.height === 'string')
  );
}

function isBreakpointOverrideValue(
  value: unknown,
): value is BreakpointOverride {
  return (
    isRecord(value) &&
    (value.position === undefined || isRecord(value.position)) &&
    (value.size === undefined || isRecord(value.size)) &&
    (value.constraints === undefined || isRecord(value.constraints)) &&
    (value.hidden === undefined || typeof value.hidden === 'boolean') &&
    (value.style === undefined || isRecord(value.style)) &&
    (value.properties === undefined || isRecord(value.properties))
  );
}

function isResponsivePositionValue(
  value: unknown,
): value is ResponsivePosition {
  if (!isRecord(value) || !isResponsiveStrategy(value.strategy)) {
    return false;
  }

  if (value.constraints !== undefined && !isRecord(value.constraints)) {
    return false;
  }

  if (value.responsiveSize !== undefined && !isRecord(value.responsiveSize)) {
    return false;
  }

  if (value.breakpoints !== undefined) {
    if (!isRecord(value.breakpoints)) {
      return false;
    }

    if (
      (value.breakpoints.mobile !== undefined &&
        !isBreakpointOverrideValue(value.breakpoints.mobile)) ||
      (value.breakpoints.tablet !== undefined &&
        !isBreakpointOverrideValue(value.breakpoints.tablet)) ||
      (value.breakpoints.desktop !== undefined &&
        !isBreakpointOverrideValue(value.breakpoints.desktop))
    ) {
      return false;
    }
  }

  return true;
}

function parseDateValue(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
}

function parseGeneratedSketchComponent(
  value: unknown,
): ParsedSketchGeneratedComponent | null {
  if (!isRecord(value)) {
    return null;
  }

  const parsed: ParsedSketchGeneratedComponent = {
    properties: {},
    style: {},
    children: [],
  };

  if (typeof value.id === 'string') {
    parsed.id = value.id;
  }
  if (typeof value.projectId === 'string') {
    parsed.projectId = value.projectId;
  }
  if (typeof value.pageId === 'string') {
    parsed.pageId = value.pageId;
  }
  if (typeof value.name === 'string') {
    parsed.name = value.name;
  }
  if (typeof value.type === 'string') {
    parsed.type = value.type;
  }
  if (isRecord(value.properties)) {
    parsed.properties = value.properties;
  }
  if (isPositionValue(value.position)) {
    parsed.position = value.position;
  }
  if (isSizeValue(value.size)) {
    parsed.size = value.size;
  }
  if (isRecord(value.style)) {
    parsed.style = value.style;
  }
  if (isResponsivePositionValue(value.responsivePosition)) {
    parsed.responsivePosition = value.responsivePosition;
  }
  if (typeof value.parentId === 'string') {
    parsed.parentId = value.parentId;
  }
  if (isStringArray(value.children)) {
    parsed.children = value.children;
  }
  if (typeof value.order === 'number') {
    parsed.order = value.order;
  }
  if (typeof value.locked === 'boolean') {
    parsed.locked = value.locked;
  }
  if (typeof value.hidden === 'boolean') {
    parsed.hidden = value.hidden;
  }
  if (typeof value.version === 'number') {
    parsed.version = value.version;
  }
  if (typeof value.createdBy === 'string') {
    parsed.createdBy = value.createdBy;
  }
  if (typeof value.lastModifiedBy === 'string') {
    parsed.lastModifiedBy = value.lastModifiedBy;
  }

  const lastModifiedAt = parseDateValue(value.lastModifiedAt);
  if (lastModifiedAt) {
    parsed.lastModifiedAt = lastModifiedAt;
  }

  const createdAt = parseDateValue(value.createdAt);
  if (createdAt) {
    parsed.createdAt = createdAt;
  }

  const updatedAt = parseDateValue(value.updatedAt);
  if (updatedAt) {
    parsed.updatedAt = updatedAt;
  }

  return parsed;
}

function parseAnnotationProcessResponse(
  value: unknown,
): ParsedAnnotationProcessResponse | null {
  if (!isRecord(value) || typeof value.success !== 'boolean') {
    return null;
  }

  const parsed: ParsedAnnotationProcessResponse = {
    success: value.success,
  };

  if (typeof value.pattern === 'boolean') {
    parsed.pattern = value.pattern;
  }

  if (typeof value.error === 'string') {
    parsed.error = value.error;
  }

  if (Array.isArray(value.components)) {
    parsed.components = value.components
      .map((component) => parseGeneratedSketchComponent(component))
      .filter(
        (component): component is ParsedSketchGeneratedComponent =>
          component !== null,
      );
  }

  const component = parseGeneratedSketchComponent(value.component);
  if (component) {
    parsed.component = component;
  }

  return parsed;
}

export function createSketchSlice<
  TState extends SketchSlice & CoreEditorSlice,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): SketchSlice {
  return {
    ...sketchInitialState,

    // Sketch/Plan mode actions
    addSketch: (sketch: SketchRectangle) => {
      set((state) => {
        state.sketches.push(sketch);
      });
    },

    updateSketch: (id: string, updates: Partial<SketchRectangle>) => {
      set((state) => {
        const sketchIndex = state.sketches.findIndex((s) => s.id === id);
        if (sketchIndex !== -1) {
          Object.assign(state.sketches[sketchIndex], updates);
        }
      });
    },

    deleteSketch: (id: string) => {
      set((state) => {
        state.sketches = state.sketches.filter((s) => s.id !== id);
        state.sketchAnnotations.delete(id);
        if (state.selectedSketchId === id) {
          state.selectedSketchId = null;
        }
      });
    },

    selectSketch: (id: string | null) => {
      set((state) => {
        state.selectedSketchId = id;
        // Deselect components when selecting a sketch
        if (id !== null) {
          state.selectedComponents = [];
        }
      });
    },

    clearSketches: () => {
      set((state) => {
        state.sketches = [];
        state.sketchAnnotations = new Map();
        state.selectedSketchId = null;
      });
    },

    setDrawingTool: (tool: 'select' | 'rectangle' | 'pan') => {
      set((state) => {
        state.drawingTool = tool;
        state.isDrawingMode = tool === 'rectangle';
      });
    },

    toggleDrawingMode: () => {
      set((state) => {
        state.isDrawingMode = !state.isDrawingMode;
        if (state.isDrawingMode) {
          state.drawingTool = 'rectangle';
        } else {
          state.drawingTool = 'select';
        }
      });
    },

    updateSketchAnnotation: (sketchId: string, annotation: AnnotationData) => {
      set((state) => {
        state.sketchAnnotations.set(sketchId, annotation);
        // Also update the annotation text on the sketch itself
        const sketch = state.sketches.find((s) => s.id === sketchId);
        if (sketch) {
          sketch.annotation = annotation.text;
        }
      });
    },

    setSelectingComponentForSketch: (selecting: boolean) => {
      set((state) => {
        state.isSelectingComponentForSketch = selecting;
      });
    },

    selectComponentTypeForSketch: (componentType: ComponentType) => {
      const state = get();
      if (!state.selectedSketchId || !state.isSelectingComponentForSketch) {
        return;
      }

      // Store the selectedSketchId in a constant to preserve type narrowing
      const selectedSketchId = state.selectedSketchId;

      // Update the annotation with the selected component type
      const existingAnnotation = state.sketchAnnotations.get(selectedSketchId);
      set((editorState) => {
        editorState.sketchAnnotations.set(selectedSketchId, {
          ...existingAnnotation,
          rectangleId: selectedSketchId,
          componentType: componentType,
          autoDetected: false,
          text: existingAnnotation?.text || '',
          properties: existingAnnotation?.properties || {},
        });
        // Clear the selecting mode
        editorState.isSelectingComponentForSketch = false;
      });
    },

    generateComponentsFromSketches: async () => {
      const state = get();
      if (state.sketches.length === 0) {
        console.warn('No sketches to generate components from');
        return;
      }

      // Start generation process
      get().setGenerationStage('preparing');

      try {
        // Add a minimum display time for preparing stage
        const preparingStartTime = Date.now();

        // Get auth token from auth store
        const { useAuthStore } = await import('@/stores/auth/auth-store');
        const authState = useAuthStore.getState();
        const accessToken = authState.tokens?.accessToken;

        if (!accessToken) {
          throw new Error('No authentication token available. Please log in.');
        }

        // Ensure preparing stage is visible for at least 800ms
        const elapsedTime = Date.now() - preparingStartTime;
        const remainingTime = Math.max(800 - elapsedTime, 0);
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        // Move to generating stage before processing sketches
        get().setGenerationStage('generating');

        const generatedComponents: Array<Component | ParsedSketchGeneratedComponent> =
          [];
        let hasPatternComponents = false; // Track if any patterns were used

        // Process each sketch individually
        for (const sketch of state.sketches) {
          const annotation = state.sketchAnnotations.get(sketch.id);

          if (annotation && annotation.text) {
            try {
              // Check if this is a template pattern FIRST
              const { templateInstantiationService } = await import(
                '@/services/ai/TemplateInstantiationService'
              );
              const template = templateInstantiationService.matchTemplatePattern(
                annotation.text,
              );

              if (template) {
                // Use our template instantiation service with sketch dimensions
                const instance =
                  await templateInstantiationService.instantiateTemplate(
                    template.metadata.id,
                    state.project?.id || '',
                    state.currentPageId || 'default',
                    { x: sketch.x, y: sketch.y },
                    { width: sketch.width, height: sketch.height },
                  );

                if (instance) {
                  // Add all template components to our generated list
                  generatedComponents.push(...instance.components);
                  hasPatternComponents = true;

                  // Skip backend call for this sketch
                  continue;
                }
              }

              // If not a template, proceed with backend processing
              const requestBody = {
                text: annotation.text,
                componentType: annotation.componentType || 'text',
                properties: annotation.properties || {},
                position: { x: sketch.x, y: sketch.y },
                size: { width: sketch.width, height: sketch.height },
                projectId: state.project?.id,
                pageId: state.currentPageId,
              };

              const response = await fetch(
                `${config.api.baseUrl}/ai/components/process-annotation`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify(requestBody),
                },
              );

              if (!response.ok) {
                throw new Error(
                  `Failed to process annotation: ${response.statusText}`,
                );
              }

              const responseData: unknown = await response.json();
              const data = parseAnnotationProcessResponse(responseData);

              if (!data) {
                console.warn(
                  `Invalid annotation response for sketch ${sketch.id}`,
                );
                continue;
              }

              // Check if this is a pattern response (multiple components)
              if (
                data.success &&
                data.pattern &&
                data.components &&
                data.components.length > 0
              ) {
                // Mark that we have pattern components (don't apply responsive layout)
                hasPatternComponents = true;

                // Add all components from the pattern
                generatedComponents.push(...data.components);
              } else if (data.success && data.component) {
                generatedComponents.push(data.component);
              } else {
                console.warn(
                  `Failed to generate component for sketch ${sketch.id}:`,
                  data.error,
                );
              }
            } catch (error) {
              console.error(`Error processing sketch ${sketch.id}:`, error);
            }
          }
        }

        if (generatedComponents.length > 0) {
          // IMPORTANT: For patterns, add ALL components at once to preserve parent-child relationships
          if (hasPatternComponents && generatedComponents.length > 0) {
            // First, transform all components
            const terraMatrixComponents: ComponentInput[] =
              generatedComponents.map((component) => {
                const validTypes: ComponentType[] = [
                  ComponentType.TEXT,
                  ComponentType.CONTAINER,
                  ComponentType.FLEX_CONTAINER,
                  ComponentType.INPUT,
                  ComponentType.BUTTON,
                  ComponentType.IMAGE,
                  ComponentType.TABLE,
                  ComponentType.LIST,
                  ComponentType.LINE_CHART,
                  ComponentType.BAR_CHART,
                  ComponentType.PIE_CHART,
                  ComponentType.GAUGE_CHART,
                  ComponentType.AREA_CHART,
                  ComponentType.SCATTER_CHART,
                  ComponentType.HEATMAP_CHART,
                  ComponentType.TREEMAP_CHART,
                  ComponentType.CHART,
                  ComponentType.CUSTOM,
                ];
                const componentType: ComponentType = validTypes.includes(
                  // component.type is string (from external source); runtime validTypes guard ensures it is a valid ComponentType
                  component.type as ComponentType,
                )
                  ? // component.type is string (from external source); runtime validTypes guard ensures it is a valid ComponentType
                    (component.type as ComponentType)
                  : ComponentType.TEXT;

                return {
                  id: component.id,
                  projectId: component.projectId || state.project?.id || '',
                  pageId:
                    component.pageId ||
                    state.currentPageId ||
                    state.project?.pages?.[0]?.id ||
                    'default',
                  name: component.name || 'Generated Component',
                  type: componentType,
                  properties: component.properties || {},
                  position: {
                    x: component.position?.x || 0,
                    y: component.position?.y || 0,
                    z: component.position?.z || 0,
                  },
                  size: component.size || { width: 100, height: 50 },
                  style: component.style || {},
                  responsivePosition:
                    component.responsivePosition ||
                    responsivePositionTranslator.generateDefaultResponsivePosition(
                      componentType,
                    ),
                  parentId: component.parentId || undefined,
                  children: component.children || [],
                  order: component.order || 0,
                  locked: component.locked || false,
                  hidden: component.hidden || false,
                  version: component.version || 1,
                  createdBy: component.createdBy || 'ai-sketch-mode',
                  lastModifiedBy:
                    component.lastModifiedBy || 'ai-sketch-mode',
                  lastModifiedAt: component.lastModifiedAt || new Date(),
                  createdAt: component.createdAt || new Date(),
                  updatedAt: component.updatedAt || new Date(),
                  isDeleted: false,
                };
              });

            // Add all components at once
            try {
              get().addMultipleComponents(terraMatrixComponents);
            } catch (error) {
              console.error('❌ Failed to add pattern components:', error);
              throw error;
            }
          } else {
            // For non-pattern components, add individually as before
            for (const component of generatedComponents) {
              // Validate component type
              const validTypes: ComponentType[] = [
                ComponentType.TEXT,
                ComponentType.CONTAINER,
                ComponentType.FLEX_CONTAINER,
                ComponentType.INPUT,
                ComponentType.BUTTON,
                ComponentType.IMAGE,
                ComponentType.TABLE,
                ComponentType.LIST,
                ComponentType.LINE_CHART,
                ComponentType.BAR_CHART,
                ComponentType.PIE_CHART,
                ComponentType.GAUGE_CHART,
                ComponentType.AREA_CHART,
                ComponentType.SCATTER_CHART,
                ComponentType.HEATMAP_CHART,
                ComponentType.TREEMAP_CHART,
                ComponentType.CHART,
                ComponentType.CUSTOM,
              ];
              const componentType: ComponentType = validTypes.includes(
                // component.type is string (from external source); runtime validTypes guard ensures it is a valid ComponentType
                component.type as ComponentType,
              )
                ? // component.type is string (from external source); runtime validTypes guard ensures it is a valid ComponentType
                  (component.type as ComponentType)
                : ComponentType.TEXT;

              const terraMatrixComponent: ComponentInput = {
                id: component.id,
                projectId: component.projectId || state.project?.id || '',
                pageId:
                  component.pageId ||
                  state.currentPageId ||
                  state.project?.pages?.[0]?.id ||
                  'default',
                name: component.name || 'Generated Component',
                type: componentType,
                properties: component.properties || {},
                position: {
                  x: component.position?.x || 0,
                  y: component.position?.y || 0,
                  z: component.position?.z || 0,
                },
                size: component.size || { width: 100, height: 50 },
                style: component.style || {},
                responsivePosition:
                  component.responsivePosition ||
                  responsivePositionTranslator.generateDefaultResponsivePosition(
                    componentType,
                  ),
                parentId: component.parentId || undefined,
                children: component.children || [],
                order: component.order || 0,
                locked: component.locked || false,
                hidden: component.hidden || false,
                version: component.version || 1,
                createdBy: component.createdBy || 'ai-sketch-mode',
                lastModifiedBy:
                  component.lastModifiedBy || 'ai-sketch-mode',
                lastModifiedAt: component.lastModifiedAt || new Date(),
                isDeleted: false,
              };

              try {
                get().addComponent(terraMatrixComponent);
              } catch (error) {
                console.error(
                  '❌ Failed to add component:',
                  error,
                  terraMatrixComponent,
                );
                throw error;
              }
            }
          }

          // Move to finalizing stage
          get().setGenerationStage('finalizing');
          const finalizingStartTime = Date.now();

          // Clear sketches after successful generation
          get().clearSketches();

          // Only apply responsive layout if we didn't use patterns
          // Patterns already have their own layout structure
          if (!hasPatternComponents) {
            await get().applyResponsiveLayout();
          }

          // Ensure finalizing stage is visible for at least 600ms
          const finalizingElapsed = Date.now() - finalizingStartTime;
          const finalizingRemaining = Math.max(600 - finalizingElapsed, 0);
          if (finalizingRemaining > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, finalizingRemaining),
            );
          }

          // Mark generation as complete
          get().setGenerationStage('complete');
        }
      } catch (error) {
        console.error('Failed to generate components from sketches:', error);
        get().setGenerationError(
          error instanceof Error
            ? error.message
            : 'Failed to generate components',
        );
        throw error;
      }
    },
  };
}
